/**
 * Blocos PEÇAS + BIPAGENS POR POSTO do Detalhe do Ambiente (Sprint P3).
 *
 * Fontes (contrato real do backend P3):
 *   - Peças: GET /api/ordens-producao?pedido={id} filtrado client-side por
 *     `pedido_ambiente` (o ViewSet ainda NÃO filtra por ambiente_producao e
 *     o serializer Lista não expõe lote/etapa — pendência de backend
 *     anotada; o slide-over da peça busca o Detalhe, que expõe tudo).
 *   - Eventos: GET /api/manufacturing-events?op={id} por peça (N requests —
 *     mesma pendência; aceitável no volume da cliente-piloto).
 *   - Postos: GET /api/postos-bipagem?ativo=true (gate posto_bipagem:view).
 *
 * Derivações:
 *   - Etapa atual da peça = primeira etapa BIPÁVEL do roteiro do ambiente
 *     sem CONCLUIR (run) nos eventos da peça.
 *   - Contador do posto = peças ativas com CONCLUIR (run) na ETAPA do posto
 *     (dois postos da mesma etapa compartilham o contador — derivação por
 *     etapa, como combinado).
 *
 * Ações da peça (slide-over): Imprimir etiqueta (gate etiqueta:imprimir —
 * reimpressão exige motivo, fluxo do 400 {reimpressao:true}), Cancelar OP
 * (gate ordem_producao:cancel + MFA, com m² aproveitável opcional — sobra
 * catalogada F8.5/ADR-0013) e Registrar reposição (gate
 * ordem_producao:reposicao + MFA recente via step-up global) — reposição
 * cria peça-filha (D3) e cancela a pai. Portadas da tela antiga de OPs
 * (removida no P5 — decisão de zero coexistência).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FiPrinter } from "react-icons/fi";
import { Link } from "react-router-dom";
import styled from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  SITUACAO_LABELS,
  SITUACAO_TONES,
  abrirEtiquetaOpEmNovaAba,
  cancelarOp,
  listarEventosBipagem,
  listarOrdensProducao,
  obterOrdemProducao,
} from "../../services/producao";
import {
  CATEGORIAS_6M,
  DECISAO_DISPOSICAO_LABELS,
  DECISAO_DISPOSICAO_TONES,
  ETAPA_LABELS,
  listarPostosBipagem,
  registrarReposicaoPeca,
} from "../../services/producaoV2";
import { formatarDataHoraCurta } from "../../utils/dataHora";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  InfoMsg,
  Pill,
  SectionTitle,
  Select,
  WarningMsg,
} from "../Orcamentos/styles";

const SITUACOES_ATIVAS = new Set(["RASCUNHO", "LIBERADA", "EM_PRODUCAO"]);

const TabelaBloco = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-3);
    padding: 8px 10px;
    border-bottom: 1px solid var(--border);
  }

  td {
    padding: 8px 10px;
    border-top: 1px solid var(--border-subtle);
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tbody tr {
    cursor: pointer;
  }
  tbody tr:hover {
    background: var(--surface-hover);
  }
`;

const Mono = styled.span`
  font-variant-numeric: tabular-nums;
`;

const GradePostos = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 10px;
`;

const CartaoPosto = styled.button`
  text-align: left;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: inherit;

  &:hover {
    border-color: var(--border-strong);
    background: var(--surface-hover);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  .nome {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .etapa {
    font-size: 11px;
    color: var(--ink-3);
  }
  .contador {
    font-size: 12px;
    color: var(--ink-2);
    font-variant-numeric: tabular-nums;
  }
`;

const ListaPecasPosto = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 4px;
    border-bottom: 1px solid var(--border-subtle);
    font-size: 13px;
    color: var(--ink-2);
    min-width: 0;
  }
  li:last-child {
    border-bottom: none;
  }
  li .peca {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const GridDados = styled.dl`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 6px 12px;
  margin: 0 0 16px;
  font-size: 13px;

  dt {
    color: var(--ink-3);
    font-weight: 600;
  }
  dd {
    margin: 0;
    color: var(--ink);
    overflow-wrap: anywhere;
  }
`;

const CampoForm = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const RotuloCampo = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
`;

const AreaTexto = styled.textarea`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.88rem;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
    border-color: transparent;
  }
`;

const CampoInput = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.88rem;
  font-family: inherit;

  &:focus {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
    border-color: transparent;
  }
`;

const DicaCampo = styled.p`
  font-size: 12px;
  color: var(--ink-3);
  margin: 4px 0 0;
`;

const BlocoAcao = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;

  h4 {
    margin: 0 0 10px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-2);
  }
`;

const RodapeAcao = styled.div`
  display: flex;
  justify-content: flex-end;
`;

function rotuloEtapa(codigo) {
  return (codigo && (ETAPA_LABELS[codigo] || codigo)) || "–";
}

// ---------------------------------------------------------------------------
// Slide-over da PEÇA (detalhe + genealogia + ações)
// ---------------------------------------------------------------------------

function PecaPanel({
  aberta, peca, filhas, onFechar, onMudou, podeCancelar, podeRepor,
  podeImprimir,
}) {
  const { request } = useAuth();
  const toast = useToast();
  const [detalhe, setDetalhe] = useState(null);
  const [erro, setErro] = useState(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");
  const [m2Aproveitavel, setM2Aproveitavel] = useState("");
  const [obsAproveitamento, setObsAproveitamento] = useState("");
  const [categoriaReposicao, setCategoriaReposicao] = useState("");
  const [descricaoReposicao, setDescricaoReposicao] = useState("");

  useEffect(() => {
    if (!aberta || !peca?.id) return;
    setDetalhe(null);
    setErro(null);
    setMotivoCancelamento("");
    setM2Aproveitavel("");
    setObsAproveitamento("");
    setCategoriaReposicao("");
    setDescricaoReposicao("");
    obterOrdemProducao(request, peca.id)
      .then(setDetalhe)
      .catch((err) =>
        setErro(err?.message || "Não foi possível carregar a peça."),
      );
  }, [aberta, peca?.id, request]);

  const d = detalhe;
  const ativa = d && SITUACOES_ATIVAS.has(d.situacao);

  // Etiqueta Code 128 (F8.3): 1ª impressão direta; reimpressão o backend
  // responde 400 {reimpressao:true} — pedimos o motivo e refazemos.
  const imprimirEtiqueta = async () => {
    try {
      await abrirEtiquetaOpEmNovaAba(request, d.id);
      toast.success("Etiqueta gerada.");
    } catch (err) {
      if (!err?.reimpressao) {
        toast.error(err?.message || "Falha ao gerar etiqueta.");
        return;
      }
      const motivo = window.prompt(
        "Esta peça já teve etiqueta impressa. Informe o motivo da reimpressão:",
        "",
      );
      if (!motivo || !motivo.trim()) {
        toast.error("Reimpressão cancelada (motivo vazio).");
        return;
      }
      try {
        await abrirEtiquetaOpEmNovaAba(request, d.id, {
          motivo: motivo.trim(),
        });
        toast.success("Etiqueta reimpressa.");
      } catch (err2) {
        toast.error(err2?.message || "Falha ao reimprimir.");
      }
    }
  };

  return (
    <SlideOverPanel
      open={aberta}
      onClose={onFechar}
      title={peca ? `Peça ${peca.numero}` : "Peça"}
      subtitle={peca?.descricao}
    >
      {erro && <ErrorMsg style={{ marginBottom: 12 }}>{erro}</ErrorMsg>}
      {!d && !erro && <EmptyMsg>Carregando…</EmptyMsg>}

      {d && (
        <>
          <GridDados>
            <dt>Situação</dt>
            <dd>
              <Pill $tone={SITUACAO_TONES[d.situacao] || "neutral"}>
                {SITUACAO_LABELS[d.situacao] || d.situacao}
              </Pill>
            </dd>
            <dt>Lote</dt>
            <dd>{d.lote_numero || "–"}</dd>
            <dt>Ambiente</dt>
            <dd>{d.ambiente_producao_numero || "–"}</dd>
            <dt>Etapa atual</dt>
            <dd>{rotuloEtapa(d.etapa_codigo_atual)}</dd>
            <dt>Pedido</dt>
            <dd>
              {d.pedido_numero}
              {d.cliente_nome ? ` · ${d.cliente_nome}` : ""}
            </dd>
            <dt>Liberada em</dt>
            <dd>
              <Mono>{formatarDataHoraCurta(d.liberada_em)}</Mono>
            </dd>
            <dt>Iniciada em</dt>
            <dd>
              <Mono>{formatarDataHoraCurta(d.iniciada_em)}</Mono>
            </dd>
            <dt>Concluída em</dt>
            <dd>
              <Mono>{formatarDataHoraCurta(d.concluida_em)}</Mono>
            </dd>
            {d.motivo_cancelamento && (
              <>
                <dt>Cancelamento</dt>
                <dd>{d.motivo_cancelamento}</dd>
              </>
            )}
            {/* Lote D: campos da decisão de disposição (quando decidida).
                Aceita shape aninhado `disposicao{}` ou flat `disposicao_*`
                — o contrato exato depende do serializer do backend. */}
            {(() => {
              const disp =
                d.disposicao
                || (d.disposicao_decisao
                  ? {
                      decisao: d.disposicao_decisao,
                      observacao: d.disposicao_observacao,
                      decidida_em: d.disposicao_decidida_em,
                      decidida_por_nome: d.disposicao_decidida_por_nome,
                    }
                  : null);
              if (!disp?.decisao) return null;
              return (
                <>
                  <dt>Disposição</dt>
                  <dd>
                    <Pill
                      $tone={
                        DECISAO_DISPOSICAO_TONES[disp.decisao] || "neutral"
                      }
                    >
                      {DECISAO_DISPOSICAO_LABELS[disp.decisao] || disp.decisao}
                    </Pill>
                    {disp.decidida_em ? (
                      <>
                        {" "}
                        <Mono>{formatarDataHoraCurta(disp.decidida_em)}</Mono>
                      </>
                    ) : null}
                    {disp.decidida_por_nome
                      ? ` · por ${disp.decidida_por_nome}`
                      : ""}
                  </dd>
                  {disp.observacao && (
                    <>
                      <dt>Obs. da disposição</dt>
                      <dd>{disp.observacao}</dd>
                    </>
                  )}
                </>
              );
            })()}
          </GridDados>

          {/* Lote D: peça reprovada na Conferência retida até o PCP
              decidir — aponta para a fila de disposição. */}
          {d.retida_disposicao && (
            <WarningMsg style={{ marginBottom: 14 }}>
              Peça retida — aguardando disposição do PCP (reprovada na
              Conferência; não aceita bipagem até a decisão).{" "}
              <Link to="/producao/disposicao">Ir para disposição</Link>
            </WarningMsg>
          )}

          {podeImprimir && (
            <RodapeAcao style={{ justifyContent: "flex-start", marginBottom: 14 }}>
              <AsyncButton
                variant="secondary"
                silentError
                silentSuccess
                onClick={imprimirEtiqueta}
              >
                <FiPrinter size={13} aria-hidden="true" /> Imprimir etiqueta
              </AsyncButton>
            </RodapeAcao>
          )}

          {/* Genealogia (D3): pai + reposição + filhas derivadas. */}
          {(d.eh_reposicao || d.peca_pai_numero) && (
            <InfoMsg style={{ marginBottom: 14 }}>
              Reposição de <Mono>{d.peca_pai_numero || "–"}</Mono>
              {d.causa_reposicao
                ? ` — causa: ${
                    CATEGORIAS_6M.find((c) => c.codigo === d.causa_reposicao)
                      ?.label || d.causa_reposicao
                  }`
                : ""}
              {d.conta_throughput === false
                ? " · fora do throughput"
                : ""}
            </InfoMsg>
          )}
          {filhas.length > 0 && (
            <InfoMsg style={{ marginBottom: 14 }}>
              Reposta por:{" "}
              {filhas.map((f) => f.numero).join(", ")}
            </InfoMsg>
          )}

          {ativa && podeRepor && (
            <BlocoAcao>
              <h4>Registrar reposição (cancela esta peça e cria a filha)</h4>
              <CampoForm>
                <RotuloCampo>Categoria da causa (6M) *</RotuloCampo>
                <Select
                  value={categoriaReposicao}
                  onChange={(e) => setCategoriaReposicao(e.target.value)}
                >
                  <option value="">Selecione…</option>
                  {CATEGORIAS_6M.map((c) => (
                    <option key={c.codigo} value={c.codigo}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </CampoForm>
              <CampoForm>
                <RotuloCampo>Descrição *</RotuloCampo>
                <AreaTexto
                  rows={2}
                  maxLength={300}
                  value={descricaoReposicao}
                  onChange={(e) => setDescricaoReposicao(e.target.value)}
                  placeholder="O que aconteceu com a peça?"
                />
              </CampoForm>
              <RodapeAcao>
                {/* Exige MFA recente — 403 abre o step-up global. */}
                <AsyncButton
                  silentError
                  disabled={!categoriaReposicao || !descricaoReposicao.trim()}
                  successMessage="Reposição registrada — peça-filha criada."
                  onClick={async () => {
                    await registrarReposicaoPeca(request, d.id, {
                      motivoCategoria: categoriaReposicao,
                      descricao: descricaoReposicao.trim(),
                    });
                    onMudou();
                    onFechar();
                  }}
                >
                  Registrar reposição
                </AsyncButton>
              </RodapeAcao>
            </BlocoAcao>
          )}

          {ativa && podeCancelar && (
            <BlocoAcao>
              <h4>Cancelar OP (sem reposição)</h4>
              <CampoForm>
                <RotuloCampo>Motivo *</RotuloCampo>
                <AreaTexto
                  rows={2}
                  maxLength={200}
                  value={motivoCancelamento}
                  onChange={(e) => setMotivoCancelamento(e.target.value)}
                  placeholder="Por que esta peça está sendo cancelada?"
                />
              </CampoForm>
              {/* F8.5 (ADR-0013): sobra catalogada quando já cortou parcial */}
              <CampoForm>
                <RotuloCampo>m² aproveitável (opcional)</RotuloCampo>
                <CampoInput
                  type="number"
                  step="0.0001"
                  min={0}
                  value={m2Aproveitavel}
                  onChange={(e) => setM2Aproveitavel(e.target.value)}
                  placeholder="0.0000"
                />
                <DicaCampo>
                  Se a chapa foi cortada parcialmente e a sobra é útil,
                  informe a área em m² — vira ChapaAproveitamento no estoque
                  para peça menor futura.
                </DicaCampo>
              </CampoForm>
              {m2Aproveitavel && Number(m2Aproveitavel) > 0 && (
                <CampoForm>
                  <RotuloCampo>Localização da sobra (opcional)</RotuloCampo>
                  <CampoInput
                    type="text"
                    maxLength={300}
                    value={obsAproveitamento}
                    onChange={(e) => setObsAproveitamento(e.target.value)}
                    placeholder="Ex.: rack 3, prateleira B"
                  />
                </CampoForm>
              )}
              <RodapeAcao>
                {/* Irreversível — exige MFA recente (step-up global). */}
                <AsyncButton
                  variant="danger"
                  silentError
                  disabled={!motivoCancelamento.trim()}
                  successMessage={
                    m2Aproveitavel && Number(m2Aproveitavel) > 0
                      ? `Peça cancelada + sobra ${m2Aproveitavel} m² catalogada.`
                      : "Peça cancelada."
                  }
                  onClick={async () => {
                    const m2 =
                      m2Aproveitavel && Number(m2Aproveitavel) > 0
                        ? m2Aproveitavel
                        : undefined;
                    await cancelarOp(request, d.id, motivoCancelamento.trim(), {
                      m2_aproveitavel: m2,
                      observacao_aproveitamento: m2
                        ? obsAproveitamento.trim()
                        : undefined,
                    });
                    onMudou();
                    onFechar();
                  }}
                >
                  Cancelar OP
                </AsyncButton>
              </RodapeAcao>
            </BlocoAcao>
          )}
        </>
      )}
    </SlideOverPanel>
  );
}

// ---------------------------------------------------------------------------
// Blocos
// ---------------------------------------------------------------------------

export default function PecasDoAmbiente({ ambiente, onRecarregarAmbiente }) {
  const { request, hasPermission } = useAuth();

  const podeVerPecas = hasPermission("ordem_producao:view");
  const podeVerPostos = hasPermission("posto_bipagem:view");
  const podeCancelar = hasPermission("ordem_producao:cancel");
  const podeRepor = hasPermission("ordem_producao:reposicao");
  const podeImprimir = hasPermission("etiqueta:imprimir");

  const [pecas, setPecas] = useState(null);
  const [eventosPorPeca, setEventosPorPeca] = useState({});
  const [postos, setPostos] = useState([]);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [pecaAberta, setPecaAberta] = useState(null);
  const [postoAberto, setPostoAberto] = useState(null);

  const carregar = useCallback(async () => {
    if (!podeVerPecas || !ambiente?.pedido) {
      setPecas([]);
      setCarregando(false);
      return;
    }
    setErro(null);
    setCarregando(true);
    try {
      const resposta = await listarOrdensProducao(request, {
        pedido: ambiente.pedido,
      });
      const todas = Array.isArray(resposta)
        ? resposta
        : resposta?.results || [];
      // Filtro client-side por ambiente (via pedido_ambiente) — o ViewSet
      // ainda não expõe ?ambiente_producao= (pendência backend).
      const rows = ambiente.pedido_ambiente
        ? todas.filter((p) => p.pedido_ambiente === ambiente.pedido_ambiente)
        : [];
      setPecas(rows);

      // Eventos de bipagem por peça (N requests — pendência backend:
      // filtro por ambiente/lote no ManufacturingEventViewSet).
      const listas = await Promise.all(
        rows.map((p) =>
          listarEventosBipagem(request, { op: p.id }).catch(() => []),
        ),
      );
      const mapa = {};
      rows.forEach((p, i) => {
        const evs = listas[i];
        mapa[p.id] = Array.isArray(evs) ? evs : evs?.results || [];
      });
      setEventosPorPeca(mapa);
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar as peças.");
    } finally {
      setCarregando(false);
    }
  }, [request, podeVerPecas, ambiente?.pedido, ambiente?.pedido_ambiente]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!podeVerPostos) return;
    listarPostosBipagem(request, { ativo: true })
      .then((resposta) =>
        setPostos(Array.isArray(resposta) ? resposta : resposta?.results || []),
      )
      .catch(() => setPostos([]));
  }, [request, podeVerPostos]);

  // Etapas bipáveis do roteiro do ambiente (com label do payload).
  const etapasBipaveis = useMemo(
    () =>
      [...(ambiente?.etapas || [])]
        .filter((e) => e.bipavel)
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    [ambiente],
  );

  // {pecaId: {etapa_codigo: timestamp do CONCLUIR run}}
  const concluidasPorPeca = useMemo(() => {
    const mapa = {};
    Object.entries(eventosPorPeca).forEach(([pecaId, eventos]) => {
      const conc = {};
      (eventos || []).forEach((ev) => {
        if (ev.acao === "CONCLUIR" && !ev.eh_setup && ev.etapa_codigo) {
          if (!conc[ev.etapa_codigo] || ev.timestamp > conc[ev.etapa_codigo]) {
            conc[ev.etapa_codigo] = ev.timestamp;
          }
        }
      });
      mapa[pecaId] = conc;
    });
    return mapa;
  }, [eventosPorPeca]);

  const etapaAtualDaPeca = useCallback(
    (p) => {
      if (p.situacao === "CONCLUIDA" || p.situacao === "CANCELADA") return null;
      const conc = concluidasPorPeca[p.id] || {};
      const proxima = etapasBipaveis.find((e) => !conc[e.codigo]);
      return proxima ? proxima.label : null;
    },
    [concluidasPorPeca, etapasBipaveis],
  );

  const pecasAtivas = useMemo(
    () => (pecas || []).filter((p) => p.situacao !== "CANCELADA"),
    [pecas],
  );

  // Filhas de reposição derivadas pela descrição gerada no backend
  // ("Reposição de {numero} — ..."), já que a Lista não expõe peca_pai.
  const filhasDe = useCallback(
    (numero) =>
      (pecas || []).filter((p) =>
        (p.descricao || "").startsWith(`Reposição de ${numero}`),
      ),
    [pecas],
  );

  const contadorDoPosto = useCallback(
    (posto) => {
      const feitas = pecasAtivas.filter(
        (p) => (concluidasPorPeca[p.id] || {})[posto.etapa_codigo],
      ).length;
      return { feitas, total: pecasAtivas.length };
    },
    [pecasAtivas, concluidasPorPeca],
  );

  const aoMudarPeca = () => {
    carregar();
    onRecarregarAmbiente?.();
  };

  if (!podeVerPecas) return null;

  return (
    <>
      <Card>
        <SectionTitle>Peças do ambiente</SectionTitle>
        {carregando ? (
          <EmptyMsg>Carregando…</EmptyMsg>
        ) : erro ? (
          <ErrorMsg>{erro}</ErrorMsg>
        ) : (pecas || []).length === 0 ? (
          <EmptyMsg>
            Nenhuma peça materializada — as peças nascem ao liberar o lote
            (Otimização &amp; Lotes).
          </EmptyMsg>
        ) : (
          <TabelaBloco>
            <colgroup>
              <col style={{ width: "16%" }} />
              <col style={{ width: "34%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "8%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Nº</th>
                <th>Descrição</th>
                <th>Lote</th>
                <th>Etapa atual</th>
                <th>Situação</th>
                <th>Refugo</th>
              </tr>
            </thead>
            <tbody>
              {pecas.map((p) => {
                const etapaAtual = etapaAtualDaPeca(p);
                const refugo = (eventosPorPeca[p.id] || []).reduce(
                  (soma, ev) => soma + (Number(ev.qtd_refugo) || 0),
                  0,
                );
                return (
                  <tr
                    key={p.id}
                    onClick={() => setPecaAberta(p)}
                    title={`${p.numero} — abrir peça`}
                  >
                    <td>
                      <Mono>{p.numero}</Mono>
                    </td>
                    <td title={p.descricao || ""}>{p.descricao || "–"}</td>
                    {/* Lote não vem na Lista (pendência backend) — o
                        slide-over mostra o lote real do Detalhe. */}
                    <td title="Abra a peça para ver o lote">–</td>
                    <td title={etapaAtual || ""}>
                      {etapaAtual ? (
                        <Pill $tone="neutral">{etapaAtual}</Pill>
                      ) : (
                        "–"
                      )}
                    </td>
                    <td
                      title={
                        p.retida_disposicao
                          ? "retida — aguardando disposição"
                          : undefined
                      }
                    >
                      <Pill $tone={SITUACAO_TONES[p.situacao] || "neutral"}>
                        {SITUACAO_LABELS[p.situacao] || p.situacao}
                      </Pill>
                      {/* Lote D: reprovada na Conferência, aguardando o
                          PCP decidir (repor/retrabalhar/aceitar). */}
                      {p.retida_disposicao && (
                        <Pill
                          $tone="danger"
                          title="retida — aguardando disposição"
                          style={{ marginLeft: 4 }}
                        >
                          retida
                        </Pill>
                      )}
                    </td>
                    <td>
                      <Mono>{refugo > 0 ? refugo : "–"}</Mono>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TabelaBloco>
        )}
      </Card>

      {podeVerPostos && (
        <Card>
          <SectionTitle>
            Bipagens por posto (contador por etapa do posto)
          </SectionTitle>
          {postos.length === 0 ? (
            <EmptyMsg>
              Nenhum posto de bipagem ativo — cadastre em Configurações da
              Produção.
            </EmptyMsg>
          ) : (
            <GradePostos>
              {postos.map((posto) => {
                const { feitas, total } = contadorDoPosto(posto);
                return (
                  <CartaoPosto
                    key={posto.id}
                    type="button"
                    onClick={() => setPostoAberto(posto)}
                    title={`${posto.nome} — ver peças na etapa ${posto.etapa_label}`}
                  >
                    <span className="nome">{posto.nome}</span>
                    <span className="etapa">{posto.etapa_label}</span>
                    <span className="contador">
                      {feitas}/{total} peças concluídas
                    </span>
                  </CartaoPosto>
                );
              })}
            </GradePostos>
          )}
        </Card>
      )}

      <PecaPanel
        aberta={Boolean(pecaAberta)}
        peca={pecaAberta}
        filhas={pecaAberta ? filhasDe(pecaAberta.numero) : []}
        onFechar={() => setPecaAberta(null)}
        onMudou={aoMudarPeca}
        podeCancelar={podeCancelar}
        podeRepor={podeRepor}
        podeImprimir={podeImprimir}
      />

      <SlideOverPanel
        open={Boolean(postoAberto)}
        onClose={() => setPostoAberto(null)}
        title={postoAberto ? postoAberto.nome : "Posto"}
        subtitle={
          postoAberto
            ? `Etapa ${postoAberto.etapa_label} · ${contadorDoPosto(postoAberto).feitas}/${contadorDoPosto(postoAberto).total} peças concluídas`
            : undefined
        }
      >
        {postoAberto && pecasAtivas.length === 0 && (
          <EmptyMsg>Nenhuma peça ativa neste ambiente.</EmptyMsg>
        )}
        {postoAberto && pecasAtivas.length > 0 && (
          <ListaPecasPosto>
            {pecasAtivas.map((p) => {
              const quando = (concluidasPorPeca[p.id] || {})[
                postoAberto.etapa_codigo
              ];
              return (
                <li key={p.id}>
                  <span className="peca" title={p.descricao || ""}>
                    <Mono>{p.numero}</Mono>
                    {p.descricao ? ` · ${p.descricao}` : ""}
                  </span>
                  {quando ? (
                    <Pill $tone="success" title={formatarDataHoraCurta(quando)}>
                      {formatarDataHoraCurta(quando)}
                    </Pill>
                  ) : (
                    <Pill $tone="neutral">pendente</Pill>
                  )}
                </li>
              );
            })}
          </ListaPecasPosto>
        )}
      </SlideOverPanel>
    </>
  );
}
